import { IconCheck, IconX } from '@tabler/icons-react';
import {
  RadioCard,
  RadioCardProps,
  RadioGroup,
} from '@zorro/design-system/v2/Radio';
import { IconHalfCircle } from '@zorro/shared/assets';
import { YesNo } from '@zorro/types';
import {
  FormErrorMessage,
  Grid,
  Icon,
  LabelWithTooltip,
  Stack,
  Text,
} from '@zorro/web/design-system';
import { ReactNode } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

import { InitialPremiumProgress } from './PaymentStatusUtils';

export type PaymentStatusField = 'initialPayment' | 'autoPay';
export type PaymentStatusVariant = 'agent' | 'employee';

// Both fields share the same card structure; the per-field differences are
// data. Labels live here so each surface stays consistent: agent surfaces
// share the status labels, the employee self-enroll drawer asks the friendly
// questions (per Figma). The agent "Set up" icon is per-field: half-circle for
// the initial premium (set up but not yet completed), check for auto-pay
// (which has no "Completed" state).
type StatusFieldConfig = {
  agentLabel: string;
  employeeLabel: string;
  notSetUpValue: string;
  setUpValue: string;
  setUpIcon: ReactNode;
  completedValue?: string;
};

const FIELD_CONFIG: Record<PaymentStatusField, StatusFieldConfig> = {
  initialPayment: {
    agentLabel: 'Initial payment status:',
    employeeLabel:
      'Did you submit initial payment details using your Zorro Pay as part of your application?',
    notSetUpValue: InitialPremiumProgress.NOT_SET_UP,
    setUpValue: InitialPremiumProgress.SET_UP,
    setUpIcon: <IconHalfCircle color="var(--color-icon-fill-tag-indigo)" />,
    completedValue: InitialPremiumProgress.COMPLETED,
  },
  autoPay: {
    agentLabel: 'Auto-pay status:',
    employeeLabel:
      'Did you set up recurring auto-pay for all future payments for your plan?',
    notSetUpValue: YesNo.NO,
    setUpValue: YesNo.YES,
    setUpIcon: <Icon icon={IconCheck} />,
  },
};

type StatusCard = {
  value: string;
  label: string;
  icon: ReactNode;
  iconColor: RadioCardProps['iconColor'];
};

const getStatusCards = (
  { notSetUpValue, setUpValue, setUpIcon, completedValue }: StatusFieldConfig,
  variant: PaymentStatusVariant,
  shouldShowCompleted?: boolean
): StatusCard[] => {
  if (variant === 'employee') {
    return [
      {
        value: setUpValue,
        label: 'Yes',
        icon: <Icon icon={IconCheck} />,
        iconColor: 'indigo',
      },
      {
        value: notSetUpValue,
        label: 'No',
        icon: <Icon icon={IconX} />,
        iconColor: 'indigo',
      },
    ];
  }

  const cards: StatusCard[] = [
    {
      value: notSetUpValue,
      label: 'Not set up',
      icon: <Icon icon={IconX} />,
      iconColor: 'indigo',
    },
    {
      value: setUpValue,
      label: 'Set up',
      icon: setUpIcon,
      iconColor: 'indigo',
    },
  ];
  if (shouldShowCompleted && completedValue) {
    cards.push({
      value: completedValue,
      label: 'Completed',
      icon: <Icon icon={IconCheck} />,
      iconColor: 'indigo',
    });
  }
  return cards;
};

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UseControllerProps<TFieldValues, TName> & {
  field: PaymentStatusField;
  variant?: PaymentStatusVariant;
  // Only meaningful for the agent initialPayment variant: adds the "Completed" card.
  shouldShowCompleted?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  onSendEvent?: (value: string) => Promise<void> | void;
};

// Single status selector for both payment-status fields. `field` picks the
// values + labels, `variant` picks the presentation (agent status cards vs
// employee yes/no question); the cards always fill the row.
export function PaymentStatusRadioCards<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  field,
  variant = 'agent',
  shouldShowCompleted,
  isRequired,
  isDisabled,
  onSendEvent,
  ...formProps
}: Props<TFieldValues, TName>) {
  const {
    field: controllerField,
    formState: { errors },
  } = useController(formProps);

  const config = FIELD_CONFIG[field];
  const cards = getStatusCards(config, variant, shouldShowCompleted);

  return (
    <>
      <Stack gap="xs">
        {variant === 'employee' ? (
          <Text c="var(--color-text-default-secondary)" fz="0.875rem">
            {config.employeeLabel}
          </Text>
        ) : (
          <LabelWithTooltip
            label={config.agentLabel}
            isRequired={isRequired}
            color="var(--color-text-default-secondary)"
          />
        )}
        <RadioGroup
          {...controllerField}
          onChange={async (value) => {
            controllerField.onChange(value);
            await onSendEvent?.(value);
          }}
        >
          <Grid>
            {cards.map(({ value, icon, label, iconColor }) => (
              <Grid.Col span={12 / cards.length} key={value}>
                <RadioCard
                  icon={icon}
                  iconColor={iconColor}
                  label={label}
                  value={value}
                  disabled={isDisabled}
                />
              </Grid.Col>
            ))}
          </Grid>
        </RadioGroup>
      </Stack>
      <FormErrorMessage fieldName={formProps.name} errors={errors} />
    </>
  );
}
